import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, Clock, Save, ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { settingsService, BusinessHours, DaySchedule } from '../../services/settingsService';
import { toast } from 'sonner';

const defaultSchedule: BusinessHours['schedule'] = {
  monday: { start: '09:00', end: '18:00', enabled: true },
  tuesday: { start: '09:00', end: '18:00', enabled: true },
  wednesday: { start: '09:00', end: '18:00', enabled: true },
  thursday: { start: '09:00', end: '18:00', enabled: true },
  friday: { start: '09:00', end: '15:00', enabled: true },
  saturday: { start: '09:00', end: '18:00', enabled: false },
  sunday: { start: '09:00', end: '18:00', enabled: false }
};

const dayLabels: Record<string, string> = {
  monday: 'Pazartesi',
  tuesday: 'Salı',
  wednesday: 'Çarşamba',
  thursday: 'Perşembe',
  friday: 'Cuma',
  saturday: 'Cumartesi',
  sunday: 'Pazar'
};

const BusinessHoursSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [enabled, setEnabled] = useState<boolean>(true);
  const [schedule, setSchedule] = useState<BusinessHours['schedule']>(defaultSchedule);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await settingsService.getBusinessHours();
      setEnabled(data.enabled ?? true);
      setSchedule(data.schedule || defaultSchedule);
    } catch (err) {
      setError('Mesai saatleri yüklenirken bir hata oluştu');
      console.error('Mesai saatleri yüklenirken hata:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      await settingsService.updateBusinessHours({
        enabled,
        schedule
      });

      toast.success('Mesai saatleri başarıyla güncellendi');
      navigate('/system-settings');
    } catch (err) {
      setError('Mesai saatleri kaydedilirken bir hata oluştu');
      toast.error('Mesai saatleri kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  // Schedule işlemleri
  const updateSchedule = (day: keyof BusinessHours['schedule'], field: keyof DaySchedule, value: string | boolean) => {
    setSchedule({
      ...schedule,
      [day]: { ...schedule[day], [field]: value }
    });
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4">
        <div className="my-8 flex justify-center">
          <Loader2 className="animate-spin" size={32} />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4">
      <div className="my-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link to="/system-settings">
            <Button variant="ghost" size="icon">
              <ArrowLeft size={20} />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Clock size={24} />
              Mesai Saatleri Ayarları
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Anons oluşturma saatlerini ayarlayın
            </p>
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Ana Switch */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Mesai Saati Kontrolü</CardTitle>
            <CardDescription>
              Devre dışı bırakılırsa anons oluşturma her zaman aktif olur
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-2">
              <Switch
                id="enabled"
                checked={enabled}
                onCheckedChange={setEnabled}
              />
              <Label htmlFor="enabled">
                {enabled ? 'Aktif - Mesai saatleri kontrolü yapılıyor' : 'Pasif - Her zaman açık'}
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Gün Bazlı Program - Schedule */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Gün Bazlı Program</CardTitle>
            <CardDescription>
              Backend kontrolü için her günün mesai saatlerini ayarlayın
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(Object.keys(schedule) as Array<keyof typeof schedule>).map((day) => (
                <div key={day} className="flex items-center gap-4 p-3 border rounded-lg">
                  <div className="w-28">
                    <span className="font-medium">{dayLabels[day]}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="time"
                      value={schedule[day].start}
                      onChange={(e) => updateSchedule(day, 'start', e.target.value)}
                      className="w-32"
                      disabled={!schedule[day].enabled}
                    />
                    <span className="text-muted-foreground">-</span>
                    <Input
                      type="time"
                      value={schedule[day].end}
                      onChange={(e) => updateSchedule(day, 'end', e.target.value)}
                      className="w-32"
                      disabled={!schedule[day].enabled}
                    />
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    <Switch
                      checked={schedule[day].enabled}
                      onCheckedChange={(checked) => updateSchedule(day, 'enabled', checked)}
                    />
                    <span className="text-sm w-12">
                      {schedule[day].enabled ? 'Açık' : 'Kapalı'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Kaydet Butonu */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} size="lg">
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Kaydediliyor...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Kaydet
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BusinessHoursSettings;
